import IndicatorVsQuoteChart from "components/charts/modern/IndicatorVsQuoteChart";
import MarketCapitalizationChart from "components/charts/modern/MarketCapitalizationChart";
import QuotesChart from "components/charts/modern/QuotesChart";
import {
  CompanyContainer,
  CompanyContentContainer,
  CompanyTabsContent,
  CompanyTabsList,
} from "components/company/v3/CompanyContainers";
import AmericanHistoricalReturnContent from "components/company/v3/american/contents/quote/AmericanHistoricalReturnContent";
import useAmericanIssuer from "components/company/v3/context/hooks/useAmericanIssuer";
import { CompanyQuotesTabEnum } from "components/company/v3/menu/enums";
import classNames from "components/ui/classNames";
import { Tabs } from "components/ui/Tabs";
import { Provider } from "jotai";
import useSelfie from "lib/hooks/useSelfie";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { DollarSignIcon, LineChartIcon, PercentIcon, TrendingUpIcon } from "lucide-react";
import { useIntl } from "react-intl";
import { ISecurityCountry } from "types/graphqlTypes";
import TwoPassRender from "components/shared/TwoPassRender";

interface AmericanQuotesBoxProps {
  tickerSymbol: string;
  tab: CompanyQuotesTabEnum;
  onChangeTab: (tab: CompanyQuotesTabEnum) => void;
}

export default function AmericanQuotesBox({ tickerSymbol, tab, onChangeTab }: AmericanQuotesBoxProps) {
  const intl = useIntl();
  const issuer = useAmericanIssuer();
  const viewer = useSelfie();

  return (
    <CompanyContainer
      className={classNames({
        "xl:h-full xl:min-h-company-box": [CompanyQuotesTabEnum.QUOTE].includes(tab),
        "xl:h-fit": [CompanyQuotesTabEnum.HISTORICAL_RETURN].includes(tab),
      })}
    >
      <CompanyContentContainer className="grow">
        <Tabs
          defaultValue={CompanyQuotesTabEnum.QUOTE}
          value={tab}
          onValueChange={(value) => onChangeTab(value as CompanyQuotesTabEnum)}
          className="h-auto w-full"
        >
          <CompanyTabsList
            tabs={[
              {
                value: CompanyQuotesTabEnum.QUOTE,
                label: intl.formatMessage({
                  defaultMessage: "Cotação",
                  description: "Aba do menu superior da empresa",
                  id: "uWNGsP",
                }),
                icon: <LineChartIcon className="inline-flex" size={16} strokeWidth={2} />,
              },
              viewer?.isPremium
                ? {
                    value: CompanyQuotesTabEnum.HISTORICAL_RETURN,
                    label: intl.formatMessage({
                      defaultMessage: "Retorno Histórico",
                      description: "Aba do menu superior da empresa",
                      id: "oM9BVy",
                    }),
                    icon: <PercentIcon className="inline-flex" size={16} strokeWidth={2} />,
                  }
                : null,
              viewer?.isPremium
                ? {
                    value: CompanyQuotesTabEnum.PRICE_VS_INDICATOR,
                    label: issuer.isREIT
                      ? intl.formatMessage({
                          defaultMessage: "Cotação x FCO",
                          id: "aIIZbE",
                          description: "Aba do menu superior da empresa",
                        })
                      : intl.formatMessage({
                          defaultMessage: "Cotação x Lucro",
                          description: "Aba do menu superior da empresa",
                          id: "N3fHP+",
                        }),
                    icon: <TrendingUpIcon className="inline-flex" size={16} strokeWidth={2} />,
                  }
                : null,
              viewer?.isPremium
                ? {
                    value: CompanyQuotesTabEnum.MARKET_CAPITALIZATION,
                    label: intl.formatMessage({
                      defaultMessage: "Valor de Mercado",
                      description: "Aba do menu superior da empresa",
                      id: "VITY4x",
                    }),
                    icon: <DollarSignIcon className="inline-flex" size={16} strokeWidth={2} />,
                  }
                : null,
            ].filter(notUndefinedOrNull)}
          />

          <CompanyTabsContent value={CompanyQuotesTabEnum.QUOTE}>
            <Provider>
              <TwoPassRender>{() => <QuotesChart tickerSymbols={[tickerSymbol]} />}</TwoPassRender>
            </Provider>
          </CompanyTabsContent>

          {viewer?.isPremium && (
            <CompanyTabsContent value={CompanyQuotesTabEnum.HISTORICAL_RETURN}>
              <AmericanHistoricalReturnContent />
            </CompanyTabsContent>
          )}

          {viewer?.isPremium && (
            <CompanyTabsContent value={CompanyQuotesTabEnum.PRICE_VS_INDICATOR}>
              <Provider>
                <IndicatorVsQuoteChart country={ISecurityCountry.Us} tickerSymbols={[tickerSymbol]} />
              </Provider>
            </CompanyTabsContent>
          )}
          {viewer?.isPremium && (
            <CompanyTabsContent value={CompanyQuotesTabEnum.MARKET_CAPITALIZATION}>
              <Provider>
                <TwoPassRender>{() => <MarketCapitalizationChart tickerSymbols={[tickerSymbol]} />}</TwoPassRender>
              </Provider>
            </CompanyTabsContent>
          )}
        </Tabs>
      </CompanyContentContainer>
    </CompanyContainer>
  );
}
